import { useState, useEffect } from 'react';
import {
  MessageSquare,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Filter,
  Plus,
  X,
  Wifi,
  WifiOff,
  AlertTriangle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import {
  getAllMessages,
  addMessage,
  updateMessage,
  deleteMessage,
  getAllContacts,
} from '../db/database';
import type { QueuedMessage, EmergencyContact, MessagePriority, MessageStatus } from '../types';

export function Messages() {
  const { isOnline, addNotification, setPendingMessages } = useApp();
  const [messages, setMessages] = useState<QueuedMessage[]>([]);
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<MessageStatus | 'all'>('all');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    recipientId: '',
    content: '',
    priority: 'normal' as MessagePriority,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const pendingCount = messages.filter(m => m.status === 'pending').length;
    setPendingMessages(pendingCount);
  }, [messages, setPendingMessages]);

  // Simulate message sending when online
  useEffect(() => {
    if (!isOnline) return;

    const interval = setInterval(async () => {
      const pending = messages.filter(m => m.status === 'pending');
      if (pending.length === 0) return;

      const message = pending[0];
      await updateMessage(message.id, { status: 'sending' });
      loadData();

      // Simulate sending delay
      setTimeout(async () => {
        const success = Math.random() > 0.2; // 80% success rate
        if (success) {
          await updateMessage(message.id, {
            status: 'sent',
            sentAt: Date.now(),
          });
          addNotification('success', 'Message Sent', `Message to ${message.recipientName} delivered`);
        } else {
          await updateMessage(message.id, {
            status: 'failed',
            retryCount: message.retryCount + 1,
          });
          addNotification('error', 'Send Failed', `Failed to send message to ${message.recipientName}`);
        }
        loadData();
      }, 1500);
    }, 3000);

    return () => clearInterval(interval);
  }, [isOnline, messages, addNotification]);

  async function loadData() {
    try {
      const [msgData, contactData] = await Promise.all([
        getAllMessages(),
        getAllContacts(),
      ]);
      setMessages(msgData.sort((a, b) => b.createdAt - a.createdAt));
      setContacts(contactData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const contact = contacts.find(c => c.id === formData.recipientId);
    if (!contact) return;

    try {
      await addMessage({
        recipientId: contact.id,
        recipientName: contact.name,
        content: formData.content,
        priority: formData.priority,
      });

      addNotification(
        isOnline ? 'info' : 'warning',
        isOnline ? 'Message Queued' : 'Saved Offline',
        isOnline
          ? 'Your message will be sent shortly'
          : 'Message saved. Will send when online.'
      );

      setShowModal(false);
      setFormData({ recipientId: '', content: '', priority: 'normal' });
      loadData();
    } catch (error) {
      console.error('Failed to queue message:', error);
      addNotification('error', 'Error', 'Failed to queue message');
    }
  }

  async function handleRetry(id: string) {
    await updateMessage(id, { status: 'pending', retryCount: 0 });
    loadData();
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this message?')) return;
    await deleteMessage(id);
    loadData();
  }

  const filteredMessages = filter === 'all'
    ? messages
    : messages.filter(m => m.status === filter);

  const getStatusIcon = (status: MessageStatus) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-warning-400" />;
      case 'sending':
        return <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />;
      case 'sent':
        return <CheckCircle className="w-4 h-4 text-safe-400" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-emergency-400" />;
    }
  };

  const getStatusText = (status: MessageStatus) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'sending':
        return 'Sending...';
      case 'sent':
        return 'Delivered';
      case 'failed':
        return 'Failed';
    }
  };

  const getPriorityColor = (priority: MessagePriority) => {
    switch (priority) {
      case 'critical':
        return 'bg-emergency-900/50 text-emergency-300 border-emergency-700';
      case 'high':
        return 'bg-warning-900/50 text-warning-300 border-warning-700';
      case 'normal':
        return 'bg-gray-700/50 text-gray-300 border-gray-600';
      case 'low':
        return 'bg-gray-800/50 text-gray-400 border-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-emergency-400" />
            Message Queue
          </h1>
          <p className="text-gray-400 mt-1">
            {isOnline ? (
              <span className="flex items-center gap-1">
                <Wifi className="w-4 h-4 text-safe-400" />
                Online - messages will be sent automatically
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <WifiOff className="w-4 h-4 text-warning-400" />
                Offline - messages will be queued
              </span>
            )}
          </p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-emergency">
          <Plus className="w-4 h-4" />
          New Message
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
        {(['all', 'pending', 'sending', 'sent', 'failed'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              filter === status
                ? 'bg-emergency-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status !== 'all' && (
              <span className="ml-1 opacity-70">
                ({messages.filter(m => m.status === status).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Messages List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-gray-700 rounded w-1/3 mb-2" />
              <div className="h-3 bg-gray-700 rounded w-full" />
            </div>
          ))}
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="card p-8 text-center">
          <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No messages</h3>
          <p className="text-gray-400 mb-4">
            {filter === 'all'
              ? 'Send your first message to get started'
              : `No ${filter} messages`}
          </p>
          {filter === 'all' && (
            <button onClick={() => setShowModal(true)} className="btn-emergency">
              <Plus className="w-4 h-4" />
              New Message
            </button>
          )}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
          {filteredMessages.map((message) => (
            <motion.div
              key={message.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`card p-4 ${
                message.status === 'failed'
                  ? 'border-l-4 border-l-emergency-500'
                  : message.status === 'pending'
                  ? 'border-l-4 border-l-warning-500'
                  : ''
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-white">{message.recipientName}</span>
                    <span className={`badge ${getPriorityColor(message.priority)}`}>
                      {message.priority}
                    </span>
                    <span className="flex items-center gap-1 text-sm">
                      {getStatusIcon(message.status)}
                      <span className={
                        message.status === 'sent' ? 'text-safe-400' :
                        message.status === 'failed' ? 'text-emergency-400' :
                        message.status === 'sending' ? 'text-blue-400' :
                        'text-warning-400'
                      }>
                        {getStatusText(message.status)}
                      </span>
                    </span>
                  </div>
                  <p className="text-gray-300 mb-2">{message.content}</p>
                  <div className="text-xs text-gray-500 flex items-center gap-3">
                    <span>Created: {new Date(message.createdAt).toLocaleString()}</span>
                    {message.sentAt && (
                      <span>Sent: {new Date(message.sentAt).toLocaleString()}</span>
                    )}
                    {message.retryCount > 0 && (
                      <span className="text-warning-400">Retries: {message.retryCount}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {message.status === 'failed' && (
                    <button
                      onClick={() => handleRetry(message.id)}
                      className="p-2 rounded-lg hover:bg-gray-700 text-warning-400 hover:text-warning-300 transition-colors"
                      title="Retry"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(message.id)}
                    className="p-2 rounded-lg hover:bg-emergency-900/50 text-gray-400 hover:text-emergency-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* New Message Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md"
            >
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">New Message</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-700 text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!isOnline && (
                <div className="mx-4 mt-4 p-3 bg-warning-900/50 border border-warning-700 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning-400 flex-shrink-0" />
                  <p className="text-sm text-warning-300">
                    You're offline. Message will be queued and sent when connection is restored.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="label">Recipient *</label>
                  {contacts.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No contacts available. Add contacts first.
                    </p>
                  ) : (
                    <select
                      required
                      value={formData.recipientId}
                      onChange={(e) => setFormData({ ...formData, recipientId: e.target.value })}
                      className="input"
                    >
                      <option value="">Select recipient</option>
                      {contacts.map((contact) => (
                        <option key={contact.id} value={contact.id}>
                          {contact.name} ({contact.relationship})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="label">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as MessagePriority })}
                    className="input"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="label">Message *</label>
                  <textarea
                    required
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    className="input min-h-[120px] resize-none"
                    placeholder="Type your emergency message..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={contacts.length === 0}
                    className="btn-emergency flex-1"
                  >
                    <Send className="w-4 h-4" />
                    {isOnline ? 'Send' : 'Queue'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
